import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAdminStats } from "@/services/api/endpoints/admin.api";
import {
  AdminDashboardStats,
  AdminStatsFilters,
  CustomerStats,
} from "@/types/admin.types";

interface AdminState {
  stats: AdminDashboardStats | null;
  currentStats: CustomerStats | null;
  selectedCustomerId: string;
  loading: boolean;
  error: string | null;
  filters: AdminStatsFilters;
}

const initialState: AdminState = {
  stats: null,
  currentStats: null,
  selectedCustomerId: "",
  loading: false,
  error: null,
  filters: {
    start_date: new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0], // Last 30 days
    end_date: new Date().toISOString().split("T")[0],
  },
};

export const fetchAdminStats = createAsyncThunk(
  "admin/fetchStats",
  async (filters: AdminStatsFilters, { rejectWithValue }) => {
    try {
      const data = await getAdminStats(filters);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admin stats"
      );
    }
  }
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AdminStatsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    selectCustomer: (state, action: PayloadAction<string>) => {
      const customerId = action.payload;
      state.selectedCustomerId = customerId;

      if (!state.stats) return;

      if (customerId === "total") {
        state.currentStats = state.stats.total;
      } else {
        const customer = state.stats.items.find(
          (c) => c.customer_id === customerId
        );
        if (customer) {
          state.currentStats = customer.stats;
        }
      }
    },
    clearAdminStats: (state) => {
      state.stats = null;
      state.currentStats = null;
      state.selectedCustomerId = "";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;

        // Auto-select logic
        if (action.payload.items && action.payload.items.length > 0) {
          // Default to first customer
          const firstCustomer = action.payload.items[0];
          state.selectedCustomerId = firstCustomer.customer_id;
          state.currentStats = firstCustomer.stats;
        } else {
          // Fallback if no items
          state.selectedCustomerId = "total";
          state.currentStats = action.payload.total;
        }
      })
      .addCase(fetchAdminStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, selectCustomer, clearAdminStats } =
  adminSlice.actions;
export default adminSlice.reducer;
